import { DetalleArchivoRecaudadoDto } from '../models/registrarRecaudacionMasiva';
import axiosInstance from '../../../../interceptors/axiosInstance';
import { safeLog } from '../../../../utils/logSafe';

export interface IniciarRecaudacionResponse {
    recaudacionId: string;
}

export interface ErrorRecaudacion {
    campo: string;
    tipoRegistro: number;
    numeroLinea: number;
    descripcionError: string;
    clave?: string;
    id?: string;
}

export interface ProgresoRecaudacionResponse {
    recaudacionId: string;
    estado: string;
    porcentajeCompletado: number;
    totalDividendos: number;
    dividendosProcesados: number;
    dividendosExitosos: number;
    dividendosRechazados: number;
    errores: number;
    tiempoTranscurrido?: string;
    ultimoError?: string;
    erroresDetallados?: ErrorRecaudacion[];
}

export class RecaudacionMasivaApiService {
    private static readonly BASE_URL = 'siscred-api-recaudacion/v1';

    static async iniciarRecaudacion(filasSeleccionadas: DetalleArchivoRecaudadoDto[]): Promise<IniciarRecaudacionResponse> {
        try {
            safeLog('Iniciando recaudación con filas seleccionadas:', filasSeleccionadas);
            if (!filasSeleccionadas || filasSeleccionadas.length === 0) {
                throw new Error('No hay filas seleccionadas para iniciar la recaudación');
            }

            const ids = filasSeleccionadas.map(fila => fila.id);
            const payload = { items: ids };

            const response = await axiosInstance.post(`${this.BASE_URL}/recaudacionmasiva/iniciar-procesamiento`, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const rawData = response.data as any;
            const recaudacionId = rawData?.recaudacionId
                ?? rawData?.RecaudacionId
                ?? rawData?.id
                ?? rawData?.Id
                ?? (typeof rawData === 'string' ? rawData : null);

            if (!recaudacionId || typeof recaudacionId !== 'string') {
                console.error('Respuesta inválida al iniciar recaudación:', rawData);
            }

            return { recaudacionId };
        } catch (error) {
            console.error('Error al iniciar recaudación:', error);
            throw error;
        }
    }

    static async obtenerProgreso(recaudacionId: string): Promise<ProgresoRecaudacionResponse> {
        try {
            const headers = {
                "X-Hide-Loader" : 'true',
            };
            const response = await axiosInstance.get(`${this.BASE_URL}/progresorecaudacion`, {
                params: { recaudacionId },
                headers
            });
            const rawData = response.data as any;

            return {
                recaudacionId: rawData?.recaudacionId ?? rawData?.RecaudacionId ?? recaudacionId,
                estado: rawData?.estado ?? rawData?.Estado ?? 'En Proceso',
                porcentajeCompletado: rawData?.porcentajeCompletado ?? rawData?.PorcentajeCompletado ?? 0,
                totalDividendos: rawData?.totalDividendos ?? rawData?.TotalDividendos ?? 0,
                dividendosProcesados: rawData?.dividendosProcesados ?? rawData?.DividendosProcesados ?? 0,
                dividendosExitosos: rawData?.dividendosExitosos ?? rawData?.DividendosExitosos ?? 0,
                dividendosRechazados: rawData?.dividendosRechazados ?? rawData?.DividendosRechazados ?? 0,
                errores: rawData?.errores ?? rawData?.Errores ?? 0,
                tiempoTranscurrido: rawData?.tiempoTranscurrido ?? rawData?.TiempoTranscurrido,
                ultimoError: rawData?.ultimoError ?? rawData?.UltimoError,
                erroresDetallados: rawData?.erroresDetallados ?? rawData?.ErroresDetallados
            };
        } catch (error) {
            console.error('Error al obtener progreso:', error);
            throw new Error('Error al obtener el progreso de la recaudación');
        }
    }

    static async cancelarRecaudacion(recaudacionId: string): Promise<{ exitoso: boolean; mensaje: string }> {
        try {
            const response = await axiosInstance.post(`${this.BASE_URL}/recaudacionmasiva/cancelar`, {
                recaudacionId: recaudacionId
            });

            return response.data;
        } catch (error) {
            console.error('Error al cancelar recaudación:', error);
            throw new Error('Error al cancelar la recaudación');
        }
    }

    static async obtenerHistorial(): Promise<any[]> {
        try {
            const response = await axiosInstance.get(`${this.BASE_URL}/recaudacionmasiva/historial`);
            return response.data;
        } catch (error) {
            console.error('Error al obtener historial:', error);
            throw new Error('Error al obtener el historial de recaudaciones');
        }
    }
}